import React from 'react';
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";

const formSchema = z.object({
  name: z.string().min(2, { message: "Name must be at least 2 characters" }),
  school: z.string().min(2, { message: "School name is required" }),
  age: z.string().min(1, { message: "Age is required" }),
  grade: z.string().min(1, { message: "Grade is required" }),
  position: z.string().min(1, { message: "Position is required" }),
  experience: z.string().min(1, { message: "Years of experience is required" }),
  aauTeam: z.string().optional(),
  isCurrentPlayer: z.boolean().default(false),
  parentName: z.string().min(2, { message: "Parent name is required" }),
  parentEmail: z.string().email({ message: "Invalid email address" }),
  parentPhone: z.string().min(10, { message: "Valid phone number is required" }),
});

const RegistrationForm = () => {
  const { toast } = useToast();
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      school: "",
      age: "",
      grade: "",
      position: "",
      experience: "",
      aauTeam: "",
      isCurrentPlayer: false,
      parentName: "",
      parentEmail: "",
      parentPhone: "",
    },
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    const subject = "New Player Registration";
    const body = `
      Name: ${values.name}
      School: ${values.school}
      Age: ${values.age}
      Grade: ${values.grade}
      Position: ${values.position}
      Years of Experience: ${values.experience}
      AAU Team: ${values.aauTeam || "N/A"}
      Current Player: ${values.isCurrentPlayer ? "Yes" : "No"}
      
      Parent Information:
      Name: ${values.parentName}
      Email: ${values.parentEmail}
      Phone: ${values.parentPhone}
    `;

    window.location.href = `mailto:talentevaluator@yahoo.com?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;

    toast({
      title: "Thank you!",
      description: "We will follow up within 24-48 hours.",
    });

    form.reset();
  };

  return (
    <div className="animate-fade-up delay-300 max-w-md mx-auto">
      <h2 className="text-2xl font-semibold text-black mb-2">Join Our Next Session</h2>
      <p className="text-gray-600 mb-6">Please register below to obtain exact training location and information.</p>
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 text-left">
          <h3 className="text-lg font-semibold text-black mb-4">Player Information</h3>
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-black">Name</FormLabel>
                <FormControl>
                  <Input placeholder="Full name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="school"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-black">School</FormLabel>
                <FormControl>
                  <Input placeholder="School name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="age"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-black">Age</FormLabel>
                  <FormControl>
                    <Input type="number" placeholder="Age" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="grade"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-black">Grade</FormLabel>
                  <FormControl>
                    <Input placeholder="Current grade" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="position"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-black">Position</FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select your primary position" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Point Guard">Point Guard (1)</SelectItem>
                    <SelectItem value="Shooting Guard">Shooting Guard (2)</SelectItem>
                    <SelectItem value="Post">Post Player (4/5)</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="experience"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-black">Years of Experience</FormLabel>
                <FormControl>
                  <Input type="number" placeholder="Years playing basketball" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="aauTeam"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-black">AAU Team (optional)</FormLabel>
                <FormControl>
                  <Input placeholder="AAU team name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="isCurrentPlayer"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="text-black">
                    Current Player
                  </FormLabel>
                </div>
              </FormItem>
            )}
          />
          
          <div className="border-t pt-4 mt-6">
            <h3 className="text-lg font-semibold text-black mb-4">Parent Information</h3>
            <FormField
              control={form.control}
              name="parentName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-black">Parent Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Parent's full name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="parentEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-black">Parent Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="parent@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="parentPhone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-black">Parent Phone</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="(123) 456-7890" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          <Button type="submit" className="w-full">
            Submit Registration
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default RegistrationForm;
